import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { Box } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_SITE_OVERVIEW_CONVERSION,
  CHART_KEY_DASHBOARD_SITE_OVERVIEW_ENGAGEMENT,
  CHART_KEY_DASHBOARD_SITE_OVERVIEW_ENGAGEMENT_TREND,
  CHART_KEY_DASHBOARD_SITE_OVERVIEW_TOP_CHANNELS,
  CHART_KEY_DASHBOARD_SITE_OVERVIEW_TRAFFIC,
} from "features/analytics/constants/chartKeys";

interface ISiteOverviewTabProps {
  attributes: ApiChartAttribute[];
}

function SiteOverviewTab({ attributes }: ISiteOverviewTabProps) {
  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SITE_OVERVIEW_TRAFFIC}
            staticAttributes={attributes}
            chart={{
              name: "Traffic",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_SESSIONS_COUNT,
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ],
                },
              ],
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SITE_OVERVIEW_ENGAGEMENT}
            staticAttributes={attributes}
            chart={{
              name: "Engagement",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                  ],
                },
              ],
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SITE_OVERVIEW_CONVERSION}
            staticAttributes={attributes}
            chart={{
              name: "Conversion",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
                  ],
                },
              ],
              displayOptions: {
                statTitle: "Total Conversions",
                statTitle2: "Conversion Rate",
              },
              comparison: true,
            }}
          />,
        ]}
      />

      <Box
        sx={{
          height: { xs: 320, md: 400 },
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_SITE_OVERVIEW_ENGAGEMENT_TREND}
          staticAttributes={attributes}
          chart={{
            name: "Sitewide Engagement Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          height: { xs: 320, md: 400 },
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_SITE_OVERVIEW_TOP_CHANNELS}
          staticAttributes={attributes}
          chart={{
            name: "Top 5 Channels by",
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_CHANNEL,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            limit: 5,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
              ],
            },
          }}
        />
      </Box>
    </>
  );
}

export default SiteOverviewTab;
